import React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { UserType, AuthResponse, ROUTES } from '../types/auth';
import { AuthFormData, UseAuthFormReturn } from '../types/form';

export const useAuthForm = (): UseAuthFormReturn => {
    const navigate = useNavigate();
    const auth = useAuth();
    const [formData, setFormData] = useState<AuthFormData>({
        email: '',
        password: '',
        fullName: '',
        userType: UserType.REQUESTER,
        inviteCode: ''
    });
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const { name, value } = e.target;
        if (name) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSelectChange = (e: SelectChangeEvent<string>): void => {
        const { name, value } = e.target;
        if (name) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleLogin = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!formData.email || !formData.password) {
            setError('Email and password are required');
            setLoading(false);
            return;
        }

        try {
            const result: AuthResponse = await auth.login({
                email: formData.email,
                password: formData.password
            });

            switch (result.user_type) {
                case UserType.WORKER:
                    navigate(ROUTES.WORKER_DASHBOARD);
                    break;
                case UserType.REQUESTER:
                    navigate(ROUTES.REQUESTER_DASHBOARD);
                    break;
                case UserType.ADMIN:
                    navigate(ROUTES.ADMIN_DASHBOARD);
                    break;
                default: {
                    const _exhaustiveCheck: never = result.user_type;
                    return _exhaustiveCheck;
                }
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Invalid email or password');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!formData.email || !formData.password || !formData.fullName) {
            setError('All fields are required');
            setLoading(false);
            return;
        }

        // Check if invite code is required but missing
        const needsInviteCode = formData.userType === UserType.WORKER || formData.userType === UserType.ADMIN;
        if (needsInviteCode && !formData.inviteCode) {
            setError('Invite code is required for worker registration');
            setLoading(false);
            return;
        }

        try {
            await auth.register({
                email: formData.email,
                password: formData.password,
                full_name: formData.fullName,
                user_type: formData.userType,
                invite_code: formData.inviteCode // Include invite code in the registration request
            });
            
            // Show success message and redirect to login
            alert('Registration successful! Please log in.');
            navigate('/login');
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return {
        formData,
        error,
        loading,
        handleInputChange,
        handleSelectChange,
        handleLogin,
        handleRegister
    };
};
export default useAuthForm;